import { useMemo } from 'react';
import type { Dayjs } from 'dayjs';
import useQueryNuriSat from '@/query/conjunctions/useQueryNuriSat';
import { convertTableData } from '@/components/astroOne/conjunctionTable/body/conjunctionTableUtils';
import { SortType } from '@/types';

export const useNuriSatellites = (
  nuriSatellitesSortBy: string,
  baseDate: Dayjs
) => {
  const sortType: SortType | undefined = useMemo(() => {
    switch (nuriSatellitesSortBy) {
      case 'DCA':
        return 'DCA';
      case 'TCA':
        return 'TCA';
      case 'Probability':
      default:
        return 'Probability';
    }
  }, [nuriSatellitesSortBy]);

  const {
    data: nuriSatellitesQueryData,
    isLoading: isNuriSatellitesLoading,
  } = useQueryNuriSat(sortType, baseDate);

  const topNuriSatellites = useMemo(() => {
    if (!nuriSatellitesQueryData || !nuriSatellitesQueryData.conjunctions) {
      return [];
    }
    const nuriSatellitesIds = [66650, 66651, 66652, 66653, 66654, 66655, 66656, 66657, 66658, 66659, 66660, 66661, 66662, 66663];
    const flattenedConjunctions = nuriSatellitesQueryData.conjunctions
      .filter((item: any) => !(nuriSatellitesIds.includes(Number(item.OBJECT2.OBJECT_DESIGNATOR)) && nuriSatellitesIds.includes(Number(item.OBJECT1.OBJECT_DESIGNATOR))))
      .map((item: any) => {
        if (
          !item ||
          !item.CONJUNCTION ||
          !item.OBJECT1 ||
          !item.OBJECT2 ||
          typeof item.CONJUNCTION.TCA !== 'string'
        ) {
          return null;
        }
        return {
          MESSAGE_ID: item.CONJUNCTION.MESSAGE_ID,
          PRIMARY_OBJECT_NAME: item.OBJECT1.OBJECT_NAME ?? 'Unknown',
          PRIMARY_OBJECT_DESIGNATOR: item.OBJECT1.OBJECT_DESIGNATOR ?? 0,
          SECONDARY_OBJECT_NAME: item.OBJECT2.OBJECT_NAME ?? 'Unknown',
          SECONDARY_OBJECT_DESIGNATOR: item.OBJECT2.OBJECT_DESIGNATOR ?? 0,
          TCA: item.CONJUNCTION.TCA,
          MISS_DISTANCE: item.CONJUNCTION.MISS_DISTANCE,
          COLLISION_PROBABILITY: item.CONJUNCTION.COLLISION_PROBABILITY ?? 0,
        };
      })
      .filter(item => item !== null);

    const tableData = convertTableData({
      conjunctions: flattenedConjunctions,
      totalCount: flattenedConjunctions.length,
      currentCount: flattenedConjunctions.length,
    });

    return tableData.slice(0, 5);
  }, [nuriSatellitesQueryData]);

  return {
    nuriSatellitesQueryData,
    topNuriSatellites,
    isNuriSatellitesLoading,
  };
};
